import { useEffect } from "react";
import { RefreshCw, Pencil, Trash2 } from "lucide-react";
import { getDigikeyTokenExpiryApi } from "actions/digikeyAction";
import iziToast from "izitoast";

// Wordmark badge + display name per distributor (colours match the prototype).
const DIST_META = {
  Mouser: { short: "Mou", name: "Mouser", bg: "#003478", fg: "#FFFFFF" },
  Digikey: { short: "DK", name: "Digi-Key", bg: "#CC0000", fg: "#FFFFFF" },
  Element14: { short: "E14", name: "Element 14", bg: "#FF6900", fg: "#FFFFFF" },
  Arrow: { short: "Arr", name: "Arrow", bg: "#FFB300", fg: "#1A1A1A" },
  TTI: { short: "TTI", name: "TTI", bg: "#0F6CBD", fg: "#FFFFFF" },
  TME: { short: "TME", name: "TME", bg: "#1F8A5B", fg: "#FFFFFF" },
  Rutronik: { short: "Rut", name: "Rutronik", bg: "#00509E", fg: "#FFFFFF" },
  TI: { short: "TI", name: "Texas Instruments", bg: "#C8102E", fg: "#FFFFFF" },
  Avnet: { short: "Avn", name: "Avnet", bg: "#5F259F", fg: "#FFFFFF" },
};

// The single key we surface on the collapsed row, per distributor.
const PRIMARY_KEY = {
  Mouser: "mouser_api_key",
  Digikey: "digikey_client_id",
  Element14: "element14_api_key",
  Arrow: "arrow_api_key",
  TTI: "tti_api_key",
  TME: "tme_token",
  Rutronik: "rutronik_api_key",
  Avnet: "avnet_api_key",
};

const DistributorKeyList = ({
  type,
  distributorValue,
  digikeyData,
  setDigikeyData,
  onEdit,
  onRemove,
}) => {
  const checkDigikeyTokenExpiry = async () => {
    const res = await getDigikeyTokenExpiryApi();
    const expired = Boolean(res?.status && res?.data?.status);
    setDigikeyData({
      isTokenExpired: expired,
      message: res?.data?.message || res?.message || "",
    });
  };

  useEffect(() => {
    if (type === "Digikey") checkDigikeyTokenExpiry();
  }, [type]);

  const meta = DIST_META[type];
  if (!meta) return null;

  const isDigikey = type === "Digikey";
  const isExpired = isDigikey && digikeyData.isTokenExpired;

  // TI reads public ti.com endpoints, so it has no key to surface - saying
  // "No key on file" there would read as a misconfiguration.
  const isKeyless = type === "TI";
  const primaryKey = distributorValue?.[PRIMARY_KEY[type]] || "";
  const digikeyDate =
    distributorValue?.digikey_refresh_token_date_time ||
    distributorValue?.digikey_token_date_time;

  const handleRemove = () => {
    if (onRemove) return onRemove(type);
    iziToast.info({
      title: meta.name,
      message: "Removing a distributor isn't available yet.",
      position: "topCenter",
    });
  };

  return (
    <div className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-3 rounded-md border border-s360-stroke1 bg-white px-3.5 py-3">
      <span
        className="w-8 h-8 rounded-md inline-flex items-center justify-center text-[11px] font-bold shrink-0"
        style={{ backgroundColor: meta.bg, color: meta.fg }}
      >
        {meta.short}
      </span>

      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-sm font-semibold text-s360-fg1 leading-[18px]">
          {meta.name}
        </span>
        <span className="text-[11px] leading-[14px] font-mono text-s360-fg3 truncate">
          {primaryKey || (isKeyless ? "No API key required" : "No key on file")}
          {isDigikey && digikeyDate ? ` · refreshed ${digikeyDate}` : ""}
        </span>
      </div>

      {isExpired ? (
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-s360-warning-bg text-s360-warning-ink shrink-0">
          <span className="h-1.5 w-1.5 rounded-full bg-s360-warning" />
          Token expired
        </span>
      ) : (
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-s360-success-bg text-s360-success-ink shrink-0">
          <span className="h-1.5 w-1.5 rounded-full bg-s360-success" />
          Active
        </span>
      )}

      <div className="inline-flex items-center gap-1 shrink-0">
        {isExpired && (
          <button
            type="button"
            title="Refresh token"
            onClick={() => onEdit?.(type)}
            className="w-7 h-7 inline-flex items-center justify-center rounded border border-s360-stroke1 text-s360-violet hover:bg-s360-violet-50 hover:border-s360-violet-100 transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        )}
        <button
          type="button"
          title="Edit"
          onClick={() => onEdit?.(type)}
          className="w-7 h-7 inline-flex items-center justify-center rounded border border-s360-stroke1 text-s360-fg2 hover:bg-s360-violet-50 hover:text-s360-violet hover:border-s360-violet-100 transition-colors"
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          title="Remove"
          onClick={handleRemove}
          className="w-7 h-7 inline-flex items-center justify-center rounded border border-s360-stroke1 text-s360-danger hover:bg-s360-danger-bg hover:border-[#F1BBC0] transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export default DistributorKeyList;
